import Axios from 'api/client';
import {
    ColumnGraphSettings,
    GraphType,
    Report,
    ReportDateRange,
    ReportSchedule,
    ResourceSchema,
} from 'types/domain';
import { PaginatedData } from 'types/pagination';
import { FilterParams } from 'types/params';

interface FetchReportsParams extends FilterParams {}
interface FetchReportSchedulesParams extends FilterParams {}
interface FetchReportParams extends FilterParams {}
interface FetchReportStatsParams extends FetchReportParams {
    graph_settings?: ColumnGraphSettings;
}

interface CreateReportParams extends Pick<Report, 'name' | 'description' | 'resource'> {
    template_id: number | null;
    query: Partial<Report['query']> | null;
}

interface CreateReportScheduleParams
    extends Omit<
        ReportSchedule,
        | 'id'
        | 'created_at'
        | 'updated_at'
        | 'created_by'
        | 'scheduled_day_of_month'
        | 'scheduled_day_of_week'
        | 'scheduled_time'
        | 'start_date'
        | 'next_run_at'
        | 'last_sent_at'
    > {
    scheduled_day_of_month?: string;
    scheduled_day_of_week?: string;
    scheduled_time?: string;
    start_date?: string;
}

interface ExportReportParams extends Partial<Pick<CreateReportParams, 'query'>> {
    schedule?: Partial<CreateReportScheduleParams>;
}

type ReportGraphFunction = 'SUM' | 'COUNT' | 'AVG';
type XAxisFormat = 'hour' | 'day' | 'month' | 'year';

interface PeriodData {
    count: number;
    date_range: ReportDateRange;
    data: [string, string | number][];
}

interface ReportGraphResponse {
    type: GraphType;
    function: ReportGraphFunction;
    format: XAxisFormat;
    labels: [string, string];
    current_period: PeriodData;
    previous_period: PeriodData | null;
}

const fetchReportSchemas = (accountId: number) =>
    Axios.get<ResourceSchema[]>(`/reporting/accounts/${accountId}/schemas`).then(
        response => response.data,
    );

const fetchReports = (accountId: number, params: FetchReportsParams = {}) => {
    const { filters, ...restParams } = params;

    return Axios.post<PaginatedData<Report>>(`/reporting/accounts/${accountId}/get_reports`, {
        ...filters,
        ...restParams,
    }).then(response => response.data);
};

const fetchReportTemplates = () =>
    Axios.get<PaginatedData<Report>>(`/reporting/reports/default-templates`).then(
        response => response.data,
    );

const fetchReportById = (accountId: number, reportId: number) =>
    Axios.get<Report>(`/reporting/accounts/${accountId}/reports/${reportId}`).then(
        response => response.data,
    );

const fetchReportData = <T>(
    accountId: number,
    reportId: number,
    params: FetchReportParams = {},
) => {
    const { eager = false, ...restParams } = params;

    return Axios.post<PaginatedData<T>>(
        `/reporting/accounts/${accountId}/reports/${reportId}/results`,
        {
            include: eager ? 'customFieldResponses,primaryAddress' : '',
            ...transformParams(restParams),
        },
    ).then(response => response.data);
};

const fetchReportStats = <T>(
    accountId: number,
    reportId: number,
    params: FetchReportStatsParams = {},
) => {
    const { eager = false, ...restParams } = params;

    return Axios.post<ReportGraphResponse>(
        `/reporting/accounts/${accountId}/reports/${reportId}/graph`,
        {
            include: eager ? 'customFieldResponses,primaryAddress' : '',
            ...transformParams(restParams),
        },
    ).then(response => response.data);
};

const createReport = (accountId: number, params: CreateReportParams) =>
    Axios.post<Report>(`/reporting/accounts/${accountId}/reports`, {
        ...params,
        query: params.query
            ? {
                  ...transformParams(params.query),
              }
            : undefined,
    }).then(response => response.data);

const updateReport = (accountId: number, reportId: number, params: Partial<CreateReportParams>) =>
    Axios.patch<Report>(`/reporting/accounts/${accountId}/reports/${reportId}`, {
        ...params,
        query: params.query
            ? {
                  ...transformParams(params.query),
              }
            : undefined,
    }).then(response => response.data);

const deleteReport = (accountId: number, reportId: number) =>
    Axios.delete(`/reporting/accounts/${accountId}/reports/${reportId}`).then(
        response => response.data,
    );

const exportReport = (
    accountId: number,
    reportId: number,
    params: ExportReportParams = { query: {}, schedule: {} },
) =>
    Axios.post(`/reporting/accounts/${accountId}/reports/${reportId}/send`, {
        ...params,
        query: params.query
            ? {
                  ...transformParams(params.query),
              }
            : undefined,
    }).then(response => response.data);

const fetchReportSchedules = (
    accountId: number,
    reportId: number,
    params: FetchReportSchedulesParams = {},
) =>
    Axios.get<PaginatedData<ReportSchedule>>(
        `/reporting/accounts/${accountId}/reports/${reportId}/schedules`,
        { params: transformParams(params) },
    ).then(response => response.data);

const fetchReportScheduleById = (accountId: number, reportId: number, scheduleId: number) =>
    Axios.get<ReportSchedule>(
        `/reporting/accounts/${accountId}/reports/${reportId}/schedules/${scheduleId}`,
    ).then(response => response.data);

const createReportSchedule = (
    accountId: number,
    reportId: number,
    params: CreateReportScheduleParams,
) =>
    Axios.post<ReportSchedule>(
        `/reporting/accounts/${accountId}/reports/${reportId}/schedules`,
        transformParams(params),
    ).then(response => response.data);

const updateReportSchedule = (
    accountId: number,
    reportId: number,
    scheduleId: number,
    params: Partial<CreateReportScheduleParams>,
) =>
    Axios.patch<ReportSchedule>(
        `/reporting/accounts/${accountId}/reports/${reportId}/schedules/${scheduleId}`,
        transformParams(params),
    ).then(response => response.data);

const deleteReportSchedule = (accountId: number, reportId: number, scheduleId: number) =>
    Axios.delete(
        `/reporting/accounts/${accountId}/reports/${reportId}/schedules/${scheduleId}`,
    ).then(response => response.data);

const transformFilters = (filters: FilterParams['filters']) => {
    if (Array.isArray(filters)) {
        return { global: filters };
    }

    return filters;
};

const transformParams = <T extends Pick<FilterParams, 'filters'>>(params: T) => ({
    ...params,
    filters: transformFilters(params.filters),
    sortBy: undefined,
    sortByDesc: undefined,
});

export {
    fetchReportSchemas,
    fetchReports,
    fetchReportData,
    fetchReportStats,
    fetchReportTemplates,
    fetchReportById,
    createReport,
    updateReport,
    deleteReport,
    exportReport,
    fetchReportSchedules,
    fetchReportScheduleById,
    createReportSchedule,
    updateReportSchedule,
    deleteReportSchedule,
};
export type {
    FetchReportsParams,
    FetchReportSchedulesParams,
    CreateReportScheduleParams,
    FetchReportParams,
    FetchReportStatsParams,
};
